import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import SlidePanel from '../shared/SlidePanel';
import toast from '../shared/Toast';
import { hapticError, hapticSuccess, hapticTick } from '../../_utils/haptics';
import { newServices } from '../../_services/newservices.service';
import { productService } from '../../_services/product.service';
import { loadTeam } from '../onboarding/setup/data/stylist';
import { saveCode, loadChoices, answerOf } from './discountStore';
import {
  cleanCode, dateSaid, draftProblems, lockedOnceUsed, money, splitFor, stateOf, nextCodeIdea, termsOf, usesOf,
  warningsFor, HISTORY_SAID, MAX_LEN,
} from './data/codes';
import { Advice, Chips, DateField, Icon, Seg, Stepper, Switch, ConfirmSheet } from './ui';

/**
 * Make or change a code. Five questions (the code, what it's worth, what it
 * covers, who can use it, when and where), then who pays on self-employed
 * stylists' services, and the small print as clients will see it. Once a code
 * has been used, what it's worth and its rules are fixed: only the book-by
 * date and the total can change, with a reason that's kept.
 */

const WEEKDAYS = [
  { value: 1, label: 'M', aria: 'Monday' }, { value: 2, label: 'T', aria: 'Tuesday' }, { value: 3, label: 'W', aria: 'Wednesday' },
  { value: 4, label: 'T', aria: 'Thursday' }, { value: 5, label: 'F', aria: 'Friday' }, { value: 6, label: 'S', aria: 'Saturday' }, { value: 0, label: 'S', aria: 'Sunday' },
];

const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

// The salon's menu: [{ id, name, services: [{ id, name, price }] }]
const loadCatalog = () => newServices.listServices({}).then((res) => {
  const cats = (res && res.data && res.data.responseData && res.data.responseData.result) || [];
  return cats.map((c) => ({
    id: String(c._id),
    name: c.categoryName || 'Services',
    services: (c.serviceList || []).filter((s) => !s.isDeactivated).map((s) => ({ id: String(s.commonServiceId), name: s.serviceName, price: s.fromPrice || s.servicePrice })),
  })).filter((c) => c.services.length);
});

// The salon's products: [{ id, name, price }]
const loadProducts = () => productService.getProductsList({ salonId: localStorage.getItem('salonId') || '', search: '' }).then((res) => {
  const data = (res && res.data && (res.data.data || res.data.responseData)) || {};
  let list = data.products || data.productList || data.result || [];
  if (!list.length && Array.isArray(data)) list = data.reduce((acc, cat) => [...acc, ...(cat.products || [])], []);
  return (Array.isArray(list) ? list : [])
    .filter((p) => p && !p.isDelete && !p.isDeleted && p.isActive !== false)
    .map((p) => ({ id: String(p._id || p.id), name: p.productName || p.name || 'Product', price: Number(p.retailPrice) || 0 }));
});

function Check({ state }) {
  return <span className={`dc-check${state === 'on' ? ' is-on' : state === 'part' ? ' is-part' : ''}`} aria-hidden="true">{state === 'on' ? <Icon.Tick /> : null}</span>;
}

function ServicePicker({ catalog, scope, onChange }) {
  const [q, setQ] = useState('');
  const cats = catalog || [];
  const catIds = new Set((scope.categoryIds || []).map(String));
  const svcIds = new Set((scope.serviceIds || []).map(String));
  const needle = q.trim().toLowerCase();
  const names = { ...(scope.names || {}) };

  const commit = (nextCats, nextSvcs) => {
    cats.forEach((c) => { names[c.id] = c.name; c.services.forEach((s) => { names[s.id] = s.name; }); });
    const keep = {};
    [...nextCats, ...nextSvcs, ...(scope.stylistIds || [])].forEach((id) => { if (names[id]) keep[id] = names[id]; });
    onChange({ ...scope, categoryIds: [...nextCats], serviceIds: [...nextSvcs], names: { ...(scope.names || {}), ...keep } });
  };
  const toggleCat = (c) => {
    hapticTick();
    const nc = new Set(catIds);
    const ns = new Set(svcIds);
    if (nc.has(c.id)) nc.delete(c.id);
    else { nc.add(c.id); c.services.forEach((s) => ns.delete(s.id)); }
    commit(nc, ns);
  };
  const toggleSvc = (c, s) => {
    hapticTick();
    const nc = new Set(catIds);
    const ns = new Set(svcIds);
    if (nc.has(c.id)) {
      nc.delete(c.id);
      c.services.forEach((x) => { if (x.id !== s.id) ns.add(x.id); });
    } else if (ns.has(s.id)) ns.delete(s.id);
    else ns.add(s.id);
    commit(nc, ns);
  };

  if (!catalog) return <p className="dc-help" style={{ margin: 0 }}>Loading your services…</p>;
  if (!cats.length) return <p className="dc-help" style={{ margin: 0 }}>No services on your menu yet.</p>;
  const shownCats = cats.map((c) => ({ ...c, services: c.services.filter((s) => !needle || s.name.toLowerCase().includes(needle) || c.name.toLowerCase().includes(needle)) })).filter((c) => c.services.length);
  const count = catIds.size + svcIds.size;
  return (
    <div className="dc-pick">
      <input className="dc-pick-search" type="search" value={q} placeholder="Search services" aria-label="Search services" onChange={(e) => { const v = e.target.value; setQ(v); }} />
      <div className="dc-pick-list">
        {shownCats.map((c) => {
          const on = catIds.has(c.id);
          const some = !on && c.services.some((s) => svcIds.has(s.id));
          return (
            <React.Fragment key={c.id}>
              <button type="button" className={`dc-pick-cat${on ? ' is-on' : ''}`} aria-pressed={on} onClick={() => toggleCat(c)}>
                <Check state={on ? 'on' : some ? 'part' : 'off'} />
                <span className="dc-pick-name">{c.name}</span>
                <span className="dc-pick-price">{on ? 'All, and new ones' : `${c.services.length}`}</span>
              </button>
              {c.services.map((s) => {
                const sOn = on || svcIds.has(s.id);
                return (
                  <button key={s.id} type="button" className={`dc-pick-item${sOn ? ' is-on' : ''}`} aria-pressed={sOn} onClick={() => toggleSvc(c, s)}>
                    <Check state={sOn ? 'on' : 'off'} />
                    <span className="dc-pick-name">{s.name}</span>
                    {s.price ? <span className="dc-pick-price">{money(s.price)}</span> : null}
                  </button>
                );
              })}
            </React.Fragment>
          );
        })}
        {!shownCats.length && <p className="dc-help" style={{ margin: 0, padding: '14px 12px' }}>Nothing matches “{q}”.</p>}
      </div>
      <div className="dc-pick-foot">{count ? `${count} chosen. A whole category includes services you add to it later.` : 'Tap a category for all of it, or choose services one by one.'}</div>
    </div>
  );
}

function ProductPicker({ products, scope, onChange }) {
  const [q, setQ] = useState('');
  const set = new Set((scope.productIds || []).map(String));
  const needle = q.trim().toLowerCase();
  const rows = (products || []).filter((p) => !needle || p.name.toLowerCase().includes(needle));
  const toggle = (p) => {
    hapticTick();
    const n = new Set(set);
    if (n.has(p.id)) n.delete(p.id); else n.add(p.id);
    onChange({ ...scope, productIds: [...n], names: { ...(scope.names || {}), [p.id]: p.name } });
  };
  if (!products) return <p className="dc-help" style={{ margin: 0 }}>Loading your products…</p>;
  if (!products.length) return <p className="dc-help" style={{ margin: 0 }}>No products in your catalogue yet.</p>;
  return (
    <div className="dc-pick">
      <input className="dc-pick-search" type="search" value={q} placeholder="Search products" aria-label="Search products" onChange={(e) => { const v = e.target.value; setQ(v); }} />
      <div className="dc-pick-list">
        {rows.map((p) => (
          <button key={p.id} type="button" className={`dc-pick-item${set.has(p.id) ? ' is-on' : ''}`} style={{ paddingLeft: 12 }} aria-pressed={set.has(p.id)} onClick={() => toggle(p)}>
            <Check state={set.has(p.id) ? 'on' : 'off'} />
            <span className="dc-pick-name">{p.name}</span>
            {p.price ? <span className="dc-pick-price">{money(p.price)}</span> : null}
          </button>
        ))}
        {!rows.length && <p className="dc-help" style={{ margin: 0, padding: '14px 12px' }}>Nothing matches “{q}”.</p>}
      </div>
      <div className="dc-pick-foot">{set.size ? `${set.size} chosen` : 'Choose the products the code takes money off.'}</div>
    </div>
  );
}

function StylistPicker({ team, ids, names, onChange }) {
  const [q, setQ] = useState('');
  const set = new Set((ids || []).map(String));
  const needle = q.trim().toLowerCase();
  const rows = (team || []).filter((t) => !needle || t.name.toLowerCase().includes(needle));
  const toggle = (t) => {
    hapticTick();
    const n = new Set(set);
    if (n.has(t.id)) n.delete(t.id); else n.add(t.id);
    onChange([...n], { ...(names || {}), [t.id]: t.name });
  };
  if (!team) return <p className="dc-help" style={{ margin: 0 }}>Loading your team…</p>;
  return (
    <div className="dc-pick">
      <input className="dc-pick-search" type="search" value={q} placeholder="Search the team" aria-label="Search the team" onChange={(e) => { const v = e.target.value; setQ(v); }} />
      <div className="dc-pick-list" style={{ maxHeight: 240 }}>
        {rows.map((t) => (
          <button key={t.id} type="button" className={`dc-pick-item${set.has(t.id) ? ' is-on' : ''}`} style={{ paddingLeft: 12 }} aria-pressed={set.has(t.id)} onClick={() => toggle(t)}>
            <Check state={set.has(t.id) ? 'on' : 'off'} />
            <span className="dc-pick-name">{t.name}</span>
            {t.selfEmployed && <span className="dc-pick-price">Self-employed</span>}
          </button>
        ))}
      </div>
      <div className="dc-pick-foot">{set.size ? `${set.size} chosen` : 'Choose who the code is for.'}</div>
    </div>
  );
}

export default function CodeEditor({ draft, codes, onClose, onSaved, onShare, onStatus }) {
  const isNew = !draft.id;
  const locked = !isNew && lockedOnceUsed(draft);
  const [d, setD] = useState(() => ({ ...draft, scope: { ...(draft.scope || {}) }, channels: { ...(draft.channels || { online: true, salon: true }) } }));
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [products, setProducts] = useState(null);
  const [team, setTeam] = useState(null);
  const [choices, setChoices] = useState({});
  const [more, setMore] = useState(!!(draft.appointmentsBy || (draft.days || []).length));
  const [showWho, setShowWho] = useState(false);
  const [askReason, setAskReason] = useState(false);
  const panel = useRef(null);
  const stylistCode = d.ownerType === 'stylist';

  useEffect(() => {
    let alive = true;
    loadCatalog().then((c) => { if (alive) setCatalog(c); }).catch(() => { if (alive) setCatalog([]); });
    loadProducts().then((p) => { if (alive) setProducts(p); }).catch(() => { if (alive) setProducts([]); });
    loadTeam().then((rows) => {
      if (!alive) return;
      setTeam((rows || []).map((r) => ({
        id: String(r._id),
        name: String(r.name || '').trim() || 'Team member',
        selfEmployed: Number(r.hairdresserInfo && r.hairdresserInfo.employmentTypeId) === 2 && Number(r.hairdresserInfo && r.hairdresserInfo.isSalonOwner) !== 1,
        cut: Number(r.hairdresserInfo && r.hairdresserInfo.salonCommissionRate) || 0,
      })));
    }).catch(() => { if (alive) setTeam([]); });
    loadChoices().then((c) => { if (alive) setChoices(c || {}); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const patch = (p) => setD((x) => ({ ...x, ...p }));
  const patchScope = (p) => setD((x) => ({ ...x, scope: { ...x.scope, ...p } }));
  const problems = draftProblems(d, codes) || {};
  const show = (k) => (tried ? problems[k] : null);
  const advice = useMemo(() => warningsFor(d, codes, new Date()), [d, codes]);
  const terms = termsOf({ ...d, code: cleanCode(d.code) || 'YOURCODE' }, { now: new Date(), long: true });

  const seTeam = (team || []).filter((t) => t.selfEmployed);
  const coveredFor = (t) => {
    const a = answerOf(choices[t.id]);
    if (a === null) return 'wait';
    if (a === 'no') return 'no';
    if (d.fundingMode === 'salon') return 'yes';
    return a === 'shared' && t.cut > 0 ? 'yes' : 'no';
  };
  const inScope = (t) => !(d.scope.stylistIds || []).length || d.scope.stylistIds.map(String).includes(t.id);
  const seInScope = seTeam.filter(inScope);
  const seYes = seInScope.filter((t) => coveredFor(t) === 'yes');
  const exampleCut = (() => {
    const rates = seTeam.map((t) => t.cut).filter((c) => c > 0).sort((a, b) => a - b);
    return rates.length ? rates[Math.floor(rates.length / 2)] : 40;
  })();
  const ex = (mode) => splitFor({ price: 100, discount: 20, salonCut: exampleCut / 100, mode });

  const endMoved = !isNew && (draft.endsOn || null) !== (d.endsOn || null);

  const doSave = async (note) => {
    setSaving(true);
    try {
      const saved = await saveCode({
        ...d,
        code: cleanCode(d.code),
        value: Number(d.value),
        minSpend: num(d.minSpend),
        totalUsesCap: num(d.totalUsesCap),
        perClientCap: num(d.perClientCap),
      }, { action: isNew ? 'created' : endMoved && locked ? 'end-moved' : 'edited', note });
      hapticSuccess();
      toast.success(isNew ? `${saved.code} is ready to share` : `${saved.code} saved`);
      onSaved(saved, isNew);
    } catch (e) {
      hapticError();
      toast.error((e && e.message) || 'That didn’t save. Try again.');
      setSaving(false);
    }
  };

  const save = () => {
    setTried(true);
    if (Object.keys(problems).length) {
      hapticError();
      const first = document.querySelector('.dc-editor .is-bad, .dc-editor .dc-err');
      if (first && first.scrollIntoView) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (endMoved && usesOf(draft) > 0) { setAskReason(true); return; }
    doSave('');
  };

  const close = () => (panel.current ? panel.current.handleClose() : onClose());
  const state = !isNew ? stateOf(draft, new Date()) : null;
  const s = draft.stats || {};

  const footer = (
    <>
      <button type="button" className="sp-btn-secondary" onClick={close}>Cancel</button>
      <button type="button" className="sp-btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : isNew ? 'Save code' : 'Save changes'}</button>
    </>
  );

  const headerRight = !isNew && ['live', 'scheduled'].includes(state) ? (
    <button type="button" className="dc-btn" onClick={() => onShare(draft)}><Icon.Share size={15} />Share</button>
  ) : null;

  return (
    <SlidePanel ref={panel} title={isNew ? 'New code' : draft.code} onClose={onClose} width={540} className="dc-editor dc-page" footer={footer} headerRight={headerRight}>
      <div className="dc-form">
        {!isNew && (
          <section className="dc-sec" aria-label="How it’s doing">
            <h3 className="dc-sec-title">How it’s doing</h3>
            <div className="dc-figs">
              <div className="dc-fig"><span>Uses</span><b>{usesOf(draft)}{draft.totalUsesCap ? <small style={{ fontSize: 12, color: '#8a8a90', fontWeight: 600 }}> of {draft.totalUsesCap}</small> : null}</b></div>
              <div className="dc-fig"><span>Taken</span><b>{money(s.revenue || 0)}</b></div>
              <div className="dc-fig"><span>Discount given</span><b>{money(s.discountGiven || 0)}</b></div>
              <div className="dc-fig"><span>New clients</span><b>{s.newClients || 0}</b></div>
            </div>
          </section>
        )}

        <section className="dc-sec" aria-labelledby="dc-s-code">
          <h3 className="dc-sec-title" id="dc-s-code">The code</h3>
          {locked ? (
            <div className="dc-locked">
              <b>{draft.code} has been used {usesOf(draft) === 1 ? 'once' : `${usesOf(draft)} times`},</b> so what it’s worth and its rules can’t change. That keeps what clients were promised. You can move the book-by date, change the total, pause it or end it.
            </div>
          ) : (
            <div className="dc-field">
              <label className="dc-label" htmlFor="dc-code">Code clients type</label>
              <div className="dc-code-row">
                <input
                  id="dc-code"
                  className={`dc-input dc-input--code${show('code') ? ' is-bad' : ''}`}
                  value={d.code}
                  maxLength={MAX_LEN + 4}
                  autoCapitalize="characters"
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="AUTUMN20"
                  onChange={(e) => { const v = e.target.value; patch({ code: cleanCode(v) }); }}
                />
                <button type="button" className="dc-btn" onClick={() => { hapticTick(); patch({ code: nextCodeIdea(d.code, d, codes.filter((c) => c.id !== d.id)) }); }}>Make one up</button>
              </div>
              {show('code') ? <p className="dc-err" role="alert">{problems.code}</p> : <p className="dc-help">Letters and numbers, {3} to {MAX_LEN} long. Clients can type it in any case.</p>}
            </div>
          )}
        </section>

        {!locked && (
          <section className="dc-sec" aria-labelledby="dc-s-worth">
            <h3 className="dc-sec-title" id="dc-s-worth">What it’s worth</h3>
            <Seg label="Kind of discount" value={d.type} onChange={(v) => patch({ type: v })} options={[{ value: 'percentage', label: '% off' }, { value: 'fixed', label: '£ off' }]} />
            <div className="dc-field">
              <div className={`dc-affix${d.type === 'fixed' ? ' dc-affix--pre' : ''}`}>
                {d.type === 'fixed' && <span className="dc-affix-pre">£</span>}
                <input
                  className={`dc-input${show('value') ? ' is-bad' : ''}`}
                  type="number"
                  inputMode={d.type === 'fixed' ? 'decimal' : 'numeric'}
                  min="1"
                  max={d.type === 'percentage' ? 100 : undefined}
                  step={d.type === 'percentage' ? 1 : 0.5}
                  value={d.value === null || d.value === undefined ? '' : d.value}
                  aria-label={d.type === 'fixed' ? 'Amount off in pounds' : 'Percentage off'}
                  onChange={(e) => { const v = e.target.value; patch({ value: v === '' ? '' : Number(v) }); }}
                />
                {d.type === 'percentage' && <span className="dc-affix-post">%</span>}
              </div>
              {show('value') && <p className="dc-err" role="alert">{problems.value}</p>}
              {d.type === 'fixed' && !show('value') && <p className="dc-help">Taken off the total, never below £0. Shared across the services it covers.</p>}
            </div>
          </section>
        )}

        {!locked && (
          <section className="dc-sec" aria-labelledby="dc-s-covers">
            <h3 className="dc-sec-title" id="dc-s-covers">What it works on</h3>
            <Seg label="Services it covers" value={d.scope.services === 'some' ? 'some' : 'all'} onChange={(v) => patchScope({ services: v })} options={[{ value: 'all', label: stylistCode ? 'All my services' : 'All services' }, { value: 'some', label: 'Chosen services' }]} />
            {d.scope.services === 'some' && <ServicePicker catalog={catalog} scope={d.scope} onChange={(sc) => setD((x) => ({ ...x, scope: sc }))} />}
            {show('scope') && <p className="dc-err" role="alert">{problems.scope}</p>}
            {!stylistCode && (
              <>
                <div className="dc-row">
                  <div className="dc-row-text"><b>Apply to products</b><span>Take the same off all products: at the till, with a booking and in your online shop.</span></div>
                  <Switch label="Apply to products" on={!!d.scope.products} onChange={(v) => patchScope({ products: v })} />
                </div>
                {d.scope.products && (
                  <>
                    <Seg label="Products it covers" value={d.scope.productScope === 'some' ? 'some' : 'all'} onChange={(v) => patchScope({ productScope: v })} options={[{ value: 'all', label: 'All products' }, { value: 'some', label: 'Chosen products' }]} />
                    {d.scope.productScope === 'some' && <ProductPicker products={products} scope={d.scope} onChange={(sc) => setD((x) => ({ ...x, scope: sc }))} />}
                    {show('products') && <p className="dc-err" role="alert">{problems.products}</p>}
                  </>
                )}
                <div className="dc-row">
                  <div className="dc-row-text"><b>Only with some of the team</b><span>{(d.scope.stylistIds || []).length ? `For bookings with ${(d.scope.stylistIds || []).map((id) => (d.scope.names || {})[id]).filter(Boolean).slice(0, 3).join(', ')}${d.scope.stylistIds.length > 3 ? ` and ${d.scope.stylistIds.length - 3} more` : ''}.` : 'Works with everyone on the team.'}</span></div>
                  <Switch label="Only with some of the team" on={(d.scope.stylistIds || []).length > 0 || d.scope.stylistsOnly === true} onChange={(v) => patchScope(v ? { stylistsOnly: true } : { stylistIds: [], stylistsOnly: false })} />
                </div>
                {(d.scope.stylistsOnly || (d.scope.stylistIds || []).length > 0) && (
                  <StylistPicker team={team} ids={d.scope.stylistIds} names={d.scope.names} onChange={(ids, names) => patchScope({ stylistIds: ids, names })} />
                )}
              </>
            )}
          </section>
        )}

        {!locked && (
          <section className="dc-sec" aria-labelledby="dc-s-who">
            <h3 className="dc-sec-title" id="dc-s-who">Who can use it</h3>
            <Chips label="Who can use it" value={d.audience} onChange={(v) => patch({ audience: v })} options={[{ value: 'everyone', label: 'Everyone' }, { value: 'new', label: 'New clients only' }]} />
            {d.audience === 'new' && <p className="dc-help">{stylistCode ? 'Clients who’ve never had a visit with you, matched on their phone number.' : 'Clients who’ve never had a visit here, matched on their phone number.'}</p>}
            <div className="dc-field" style={{ marginTop: 6 }}>
              <span className="dc-label">Uses per client</span>
              <Seg label="Uses per client" value={d.perClientCap ? 'limit' : 'any'} onChange={(v) => patch({ perClientCap: v === 'limit' ? 1 : null })} options={[{ value: 'limit', label: 'Limited' }, { value: 'any', label: 'No limit' }]} />
              {d.perClientCap ? (
                <div className="dc-row">
                  <div className="dc-row-text"><b>Each client can use it</b><span>Counted by phone number, so a new account doesn’t reset it.</span></div>
                  <Stepper label="Uses per client" value={Number(d.perClientCap) || 1} min={1} max={20} said={(v) => (v === 1 ? 'Once' : v === 2 ? 'Twice' : `${v} times`)} onChange={(v) => patch({ perClientCap: v })} />
                </div>
              ) : <p className="dc-help" style={{ marginTop: 0 }}>Clients can use it as often as they like.</p>}
            </div>
          </section>
        )}

        <section className="dc-sec" aria-labelledby="dc-s-limits">
          <h3 className="dc-sec-title" id="dc-s-limits">{locked ? 'Limits and dates' : 'Limits'}</h3>
          <div className="dc-row">
            <div className="dc-row-text"><b>Total uses</b><span>{d.totalUsesCap ? 'It stops working once they’re used.' : 'No limit on how many times it’s used.'}</span></div>
            <Switch label="Total uses" on={d.totalUsesCap !== null && d.totalUsesCap !== undefined} onChange={(v) => patch({ totalUsesCap: v ? Math.max(usesOf(draft) + 10, 50) : null })} />
          </div>
          {d.totalUsesCap !== null && d.totalUsesCap !== undefined && (
            <div className="dc-field">
              <input className={`dc-input${show('totalUsesCap') ? ' is-bad' : ''}`} type="number" inputMode="numeric" min={Math.max(1, usesOf(draft))} value={d.totalUsesCap} aria-label="Total uses" onChange={(e) => { const v = e.target.value; patch({ totalUsesCap: v === '' ? '' : Number(v) }); }} />
            </div>
          )}
          {show('totalUsesCap') && <p className="dc-err" role="alert">{problems.totalUsesCap}</p>}
          {!locked && (
            <>
              <div className="dc-row">
                <div className="dc-row-text"><b>Minimum spend</b><span>{d.minSpend ? `On the services it covers, before the code. Tips don’t count.` : 'Works on any amount.'}</span></div>
                <Switch label="Minimum spend" on={d.minSpend !== null && d.minSpend !== undefined} onChange={(v) => patch({ minSpend: v ? 30 : null })} />
              </div>
              {d.minSpend !== null && d.minSpend !== undefined && (
                <div className="dc-affix dc-affix--pre">
                  <span className="dc-affix-pre">£</span>
                  <input className={`dc-input${show('minSpend') ? ' is-bad' : ''}`} type="number" inputMode="decimal" min="1" value={d.minSpend} aria-label="Minimum spend in pounds" onChange={(e) => { const v = e.target.value; patch({ minSpend: v === '' ? '' : Number(v) }); }} />
                </div>
              )}
              {show('minSpend') && <p className="dc-err" role="alert">{problems.minSpend}</p>}
            </>
          )}
        </section>

        <section className="dc-sec" aria-labelledby="dc-s-when">
          <h3 className="dc-sec-title" id="dc-s-when">When</h3>
          <div className="dc-two">
            <div className="dc-field">
              <label className="dc-label" htmlFor="dc-starts">Starts</label>
              <DateField id="dc-starts" value={d.startsOn} disabled={locked} min={isNew ? moment().format('YYYY-MM-DD') : undefined} minSaid="today or later" onChange={(v) => patch({ startsOn: v })} />
            </div>
            <div className="dc-field">
              <label className="dc-label" htmlFor="dc-ends">Book by</label>
              <DateField id="dc-ends" value={d.endsOn} bad={!!show('endsOn')} placeholder="No end date" min={d.startsOn || undefined} minSaid="a date on or after the start" onChange={(v) => patch({ endsOn: v })} onClear={() => patch({ endsOn: null })} />
            </div>
          </div>
          {show('endsOn') ? <p className="dc-err" role="alert">{problems.endsOn}</p> : (
            <p className="dc-help">{d.endsOn ? `Clients can book with it until the end of ${dateSaid(d.endsOn)}. Bookings made by then keep the price, whatever the appointment date.` : 'Leave Book by empty for a code with no end date, like a welcome offer.'}</p>
          )}
          {!locked && !more && <button type="button" className="dc-more" onClick={() => { hapticTick(); setMore(true); }}>Limit appointment dates or days</button>}
          {!locked && more && (
            <>
              <div className="dc-field">
                <label className="dc-label" htmlFor="dc-appt">Appointments by <span style={{ fontWeight: 400, color: '#8a8a90' }}>(optional)</span></label>
                <DateField id="dc-appt" value={d.appointmentsBy} placeholder="Any appointment date" min={d.startsOn || undefined} minSaid="a date on or after the start" onChange={(v) => patch({ appointmentsBy: v })} onClear={() => patch({ appointmentsBy: null })} />
              </div>
              <div className="dc-field">
                <span className="dc-label" id="dc-days-l">Only on these days <span style={{ fontWeight: 400, color: '#8a8a90' }}>(optional)</span></span>
                <Chips day multiple label="Appointment days" value={d.days || []} onChange={(v) => patch({ days: v })} options={WEEKDAYS} />
                <p className="dc-help">For quieter days, such as a midweek offer. None chosen means any day.</p>
              </div>
            </>
          )}
        </section>

        {!locked && (
          <section className="dc-sec" aria-labelledby="dc-s-where">
            <h3 className="dc-sec-title" id="dc-s-where">Where it works</h3>
            <div className="dc-row">
              <div className="dc-row-text"><b>Booking online</b><span>Clients type it when they book, or open its link.</span></div>
              <Switch label="Booking online" on={!!d.channels.online} onChange={(v) => setD((x) => ({ ...x, channels: { ...x.channels, online: v } }))} />
            </div>
            <div className="dc-row">
              <div className="dc-row-text"><b>In person</b><span>At checkout and the till.</span></div>
              <Switch label="In person" on={!!d.channels.salon} onChange={(v) => setD((x) => ({ ...x, channels: { ...x.channels, salon: v } }))} />
            </div>
            {show('channels') && <p className="dc-err" role="alert">{problems.channels}</p>}
          </section>
        )}

        {!locked && !stylistCode && seTeam.length > 0 && (
          <section className="dc-sec" aria-labelledby="dc-s-pays">
            <h3 className="dc-sec-title" id="dc-s-pays">Who pays on self-employed stylists’ services</h3>
            <div className="dc-opts" role="radiogroup" aria-labelledby="dc-s-pays">
              <button type="button" role="radio" aria-checked={d.fundingMode !== 'salon'} className={`dc-opt${d.fundingMode !== 'salon' ? ' is-on' : ''}`} onClick={() => { hapticTick(); patch({ fundingMode: 'shared' }); }}>
                <span className="dc-radio" />
                <span><b>Shared, in line with their split</b><span>On a £100 service with a £20 code, a stylist who pays you {exampleCut}% gets {money(ex('shared').stylist)} instead of {money(ex('salon').stylist)}, and you get {money(ex('shared').salon)}. Only for stylists who’ve agreed to share.</span></span>
              </button>
              <button type="button" role="radio" aria-checked={d.fundingMode === 'salon'} className={`dc-opt${d.fundingMode === 'salon' ? ' is-on' : ''}`} onClick={() => { hapticTick(); patch({ fundingMode: 'salon', totalUsesCap: d.totalUsesCap || 50 }); }}>
                <span className="dc-radio" />
                <span><b>The business owner pays</b><span>Stylists get their full share of the price and you cover the discount. Where you take 0%, you’ll owe them the discount, shown in Commissions.</span></span>
              </button>
            </div>
            <div className="dc-who">
              <div className="dc-who-row">
                <span>{seInScope.length ? `Works on ${seYes.length} of ${seInScope.length} self-employed stylists’ services` : 'No self-employed stylists in this code'}</span>
                {seInScope.length > 0 && <button type="button" className="dc-more" onClick={() => setShowWho((v) => !v)}>{showWho ? 'Hide' : 'See who'}</button>}
              </div>
              {showWho && seInScope.map((t) => {
                const c = coveredFor(t);
                return (
                  <div className="dc-who-row" key={t.id}>
                    <span>{t.name}</span>
                    <span className={`dc-tag dc-tag--${c}`}>{c === 'yes' ? 'Yes' : c === 'wait' ? 'Not chosen yet' : 'No'}</span>
                  </div>
                );
              })}
            </div>
            <p className="dc-help" style={{ marginTop: 0 }}>Each stylist chooses in Settings, Your choices. Until they do, your codes don’t work on their services.</p>
          </section>
        )}

        {stylistCode && !locked && (
          <section className="dc-sec">
            <h3 className="dc-sec-title">Who pays</h3>
            <div className="dc-locked">Your code comes out of your takings, never the business owner’s. It only works on your services.</div>
          </section>
        )}

        <section className="dc-sec" aria-labelledby="dc-s-see">
          <h3 className="dc-sec-title" id="dc-s-see">Clients will see</h3>
          <div className="dc-terms" aria-live="polite">{terms}</div>
          <p className="dc-help" style={{ marginTop: 0 }}>Written from your answers. It goes with the code into posts, emails, texts and the booking page.</p>
          {advice.map((a) => <Advice key={a.id}>{a.text}</Advice>)}
        </section>

        {!isNew && (draft.history || []).length > 0 && (
          <section className="dc-sec" aria-labelledby="dc-s-hist">
            <h3 className="dc-sec-title" id="dc-s-hist">History</h3>
            <div className="dc-hist">
              {[...draft.history].reverse().map((h, i) => (
                <div className="dc-hist-row" key={`${h.at}-${i}`}>
                  <b>{HISTORY_SAID[h.action] || 'Changed'}{h.byName ? ` by ${h.byName}` : ''}</b>
                  <time dateTime={h.at}>{moment(h.at).format('D MMM YYYY, h:mma')}</time>
                  {h.note ? <small>{h.note}</small> : null}
                </div>
              ))}
            </div>
          </section>
        )}

        {!isNew && state !== 'ended' && state !== 'used-up' && (
          <section className="dc-sec">
            <div className="dc-chips">
              {state === 'live' && <button type="button" className="dc-btn" onClick={() => onStatus(draft, 'paused')}>Pause</button>}
              {state === 'paused' && <button type="button" className="dc-btn" onClick={() => onStatus(draft, 'live')}>Turn back on</button>}
              <button type="button" className="dc-btn" style={{ color: '#b3261e' }} onClick={() => onStatus(draft, 'ended')}>End now</button>
            </div>
          </section>
        )}
      </div>

      {askReason && (
        <ConfirmSheet
          title="Why is the date moving?"
          text={`${draft.code} has been used, so the change is kept with a reason. Moving the end of an offer can make it look like the deadline wasn’t real, so only do it when you have to.`}
          confirm="Save change"
          reason={{ label: 'Reason', placeholder: 'For example, we were closed for a week', required: true }}
          onCancel={() => setAskReason(false)}
          onConfirm={(note) => { setAskReason(false); doSave(note); }}
        />
      )}
    </SlidePanel>
  );
}
